import { createFileRoute, Link } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { useState } from 'react'
import { useIsMobile } from '@/lib/hooks'
import { CymaticReveal } from '@/components/CymaticReveal'
import { COLORS, FONTS, EASE_OUT, sectionInner } from '@/components/home/tokens'
import { CtaButton } from '@/components/home/CtaButton'
import {
  LISTED_BLOG_POSTS,
  UNLISTED_BLOG_POSTS,
  formatAuthors,
  type BlogPost,
} from '@/lib/blogPosts'

function BlogHeader() {
  return (
    <div style={{ ...sectionInner, marginBottom: 88, textAlign: 'left' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE_OUT }}
        style={{ maxWidth: 760 }}
      >
        <h1
          style={{
            fontFamily: FONTS.heading,
            fontWeight: 200,
            fontSize: 'var(--text-6xl)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: COLORS.neutral2,
            margin: 0,
          }}
        >
          Signal
        </h1>
        <p
          style={{
            fontFamily: FONTS.body,
            fontWeight: 400,
            fontSize: 'var(--text-base)',
            lineHeight: 1.5,
            color: COLORS.neutral5,
            margin: '24px 0 0',
            maxWidth: 372,
          }}
        >
          How we think about security and intent.
        </p>
      </motion.div>
    </div>
  )
}

export const Route = createFileRoute('/blog')({
  component: BlogIndexPage,
})

// Local filenames resolve against /images/blog/; absolute/protocol-relative
// URLs (e.g. HubSpot's CDN) are used as-is.
const ASSET = (src: string) =>
  /^(https?:)?\/\//.test(src) ? src : `${import.meta.env.BASE_URL}images/blog/${src}`

function PostCard({ post }: { post: BlogPost }) {
  const [hovered, setHovered] = useState(false)
  return (
    <article>
      <Link
        to="/blog/$slug/"
        params={{ slug: post.slug }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ display: 'flex', flexDirection: 'column', gap: 26, textDecoration: 'none' }}
      >
        <motion.div
          animate={{ y: hovered ? -6 : 0 }}
          transition={{ duration: 0.4, ease: EASE_OUT }}
          // No overflow clip here: the reveal canvas overflows the card so the
          // particles can start out on the surrounding background.
          style={{ position: 'relative', aspectRatio: '456 / 270' }}
        >
          {/* Particles gather from the background, form the card, then resolve to the photo */}
          <CymaticReveal src={ASSET(post.image)} alt={post.title} style={{ width: '100%', height: '100%' }}>
            {/* Light-green wash that smoothly fades in on hover (Exclude blend) */}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                opacity: hovered ? 1 : 0,
                transition: 'opacity 0.4s ease',
                backgroundColor: COLORS.green1,
                mixBlendMode: 'exclusion',
                pointerEvents: 'none',
              }}
            />
          </CymaticReveal>
        </motion.div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 8px' }}>
          <motion.h2
            animate={{ color: hovered ? COLORS.green1 : COLORS.neutral1 }}
            transition={{ duration: 0.3, ease: EASE_OUT }}
            style={{
              fontFamily: FONTS.body,
              fontWeight: 500,
              fontSize: 'var(--text-xl)',
              lineHeight: 1.1,
              margin: 0,
            }}
          >
            {post.title}
          </motion.h2>
          <p
            style={{
              fontFamily: FONTS.body,
              fontWeight: 400,
              fontSize: 'var(--text-sm)',
              lineHeight: 1.55,
              color: COLORS.neutral5,
              margin: 0,
            }}
          >
            {formatAuthors(post.authors)}
          </p>
        </div>
      </Link>
    </article>
  )
}

function BlogIndexPage() {
  const isMobile = useIsMobile()
  // Unlisted posts stay out of the grid until the reader asks for them; the
  // button hides once they're shown, or when there are none to show.
  const [showAll, setShowAll] = useState(false)
  const posts = showAll ? [...LISTED_BLOG_POSTS, ...UNLISTED_BLOG_POSTS] : LISTED_BLOG_POSTS

  return (
    <section style={{ position: 'relative', zIndex: 10, padding: '180px 0 120px' }}>
      <BlogHeader />
      <div
        style={{
          ...sectionInner,
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
          columnGap: 20,
          rowGap: isMobile ? 56 : 72,
        }}
      >
        {posts.map((post) => (
          <PostCard key={post.slug} post={post} />
        ))}
      </div>
      {!showAll && UNLISTED_BLOG_POSTS.length > 0 && (
        <div style={{ ...sectionInner, display: 'flex', justifyContent: 'center', marginTop: 88 }}>
          <CtaButton label="Show all posts" onClick={() => setShowAll(true)} />
        </div>
      )}
    </section>
  )
}
